/**
 * 店铺本地「属性库」：保存/套用类目产品属性（type 3/4），与平台 schema 缓存区分。
 */

import {
  findAttributeValueByLabel,
  isBlankTiktokAttributeValueId,
  resolveSkcColorAgainstTemplate,
} from '@/tiktok-platform/utils/tiktokAttributeTemplate';
import { skuSizeKey } from '@/tiktok-platform/utils/tiktokSizeChart';

export function isFilledProductAttributeValue(value) {
  if (value == null || value === '') return false;
  if (Array.isArray(value)) {
    if (!value.length) return false;
    return value.some((row) => {
      if (row == null || row === '') return false;
      if (typeof row === 'object') {
        const valueId = row.valueId ?? row.attributeValueId;
        const percent = row.percent ?? row.attributeExtraValue;
        if (valueId != null && String(valueId).trim() !== '') return true;
        if (percent != null && String(percent).trim() !== '') return true;
        return false;
      }
      return String(row).trim() !== '';
    });
  }
  if (typeof value === 'object') {
    const valueId = value.valueId ?? value.attributeValueId;
    const percent = value.percent ?? value.attributeExtraValue;
    if (valueId != null && String(valueId).trim() !== '') return true;
    if (percent != null && String(percent).trim() !== '') return true;
    return false;
  }
  return String(value).trim() !== '';
}

export function countFilledProductAttributes(productAttributes = {}) {
  return Object.values(productAttributes || {}).filter(isFilledProductAttributeValue).length;
}

/** 仅保留已填写项，供模板落库 */
export function pickFilledProductAttributes(productAttributes = {}) {
  const out = {};
  Object.entries(productAttributes || {}).forEach(([id, value]) => {
    if (!id || !isFilledProductAttributeValue(value)) return;
    out[String(id)] = value;
  });
  return out;
}

/**
 * 属性库落库：只保留当前类目 attrDefs 内、且已填写的属性（剔除旧类目/错类型残留）。
 */
export function buildProductAttributeTemplatePayload({
  productAttributes = {},
  attrDefs = [],
  productTypeId = '',
  categoryId = '',
  categoryPathLabel = '',
} = {}) {
  const filled = pickFilledProductAttributes(productAttributes);
  const defsList = Array.isArray(attrDefs) ? attrDefs : [];
  const allowIds = new Set(
    defsList
      .map((attr) => String(attr?.attributeId ?? '').trim())
      .filter(Boolean),
  );
  // 严格按当前类目 attrDefs 裁剪，剔除错店铺类型/旧类目残留属性
  const scoped = Object.fromEntries(
    Object.entries(filled).filter(([id]) => allowIds.has(String(id))),
  );
  const filledIds = new Set(Object.keys(scoped));
  const defs = defsList
    .filter((attr) => attr?.attributeId != null && filledIds.has(String(attr.attributeId)))
    .map((attr) => ({
      attributeId: String(attr.attributeId),
      name: attr.nameCn || attr.name || attr.attributeName || '',
      mode: attr.mode,
      required: Boolean(attr.required),
    }));
  return {
    productAttributes: scoped,
    attrDefs: defs,
    productTypeId: productTypeId ? String(productTypeId) : '',
    categoryId: categoryId ? String(categoryId) : '',
    categoryPathLabel: categoryPathLabel || '',
  };
}

/**
 * 套用模板：只取模板中有值、且属于当前类目属性定义的 key。
 * 调用方应对 productAttributes 做整表替换（不要与旧值 merge），
 * 模板未包含的属性视为清空。
 * @returns {{ patch: Record<string, any>, filled: number }}
 */
export function applyProductAttributeTemplatePayload(payload, allowedAttributeIds = []) {
  const src = payload?.productAttributes && typeof payload.productAttributes === 'object'
    ? payload.productAttributes
    : {};
  const allow = new Set(
    (Array.isArray(allowedAttributeIds) ? allowedAttributeIds : [])
      .map((id) => String(id || '').trim())
      .filter(Boolean),
  );
  const patch = {};
  let filled = 0;
  Object.entries(src).forEach(([id, value]) => {
    const key = String(id || '').trim();
    if (!key) return;
    if (allow.size > 0 && !allow.has(key)) return;
    if (!isFilledProductAttributeValue(value)) return;
    patch[key] = value;
    filled += 1;
  });
  return { patch, filled };
}

function collectCategoryProductAttrDefs(attrTpl) {
  const defs = [];
  const seen = new Set();
  if (!attrTpl || typeof attrTpl !== 'object') return defs;
  [
    attrTpl.productAttrs,
    attrTpl.requiredProduct,
    attrTpl.mainAttrs,
    attrTpl.otherAttrs,
  ].forEach((rows) => {
    (Array.isArray(rows) ? rows : []).forEach((attr) => {
      const id = String(attr?.attributeId ?? '').trim();
      if (!id || seen.has(id)) return;
      seen.add(id);
      defs.push(attr);
    });
  });
  return defs;
}

/** 当前类目属性模板中的商品属性 ID（不含销售/尺码） */
export function listCategoryProductAttributeIds(attrTpl) {
  return collectCategoryProductAttrDefs(attrTpl).map((attr) => String(attr.attributeId));
}

/**
 * 丢掉不属于当前类目属性模板的填写项。
 * 官方模板按全局类目填写，店铺 query-attribute-template 往往更少；不丢就会把多余属性提交给平台。
 * attrTpl 未传入时不改，避免模板未加载时误清空。
 */
export function filterProductAttributesToCategory(productAttributes = {}, attrTpl) {
  if (attrTpl == null || typeof attrTpl !== 'object') return productAttributes || {};
  const allow = new Set(listCategoryProductAttributeIds(attrTpl));
  const src = productAttributes && typeof productAttributes === 'object' ? productAttributes : {};
  const next = {};
  let dropped = false;
  Object.entries(src).forEach(([id, value]) => {
    const key = String(id || '').trim();
    if (!key) {
      dropped = true;
      return;
    }
    if (!allow.has(key)) {
      dropped = true;
      return;
    }
    next[key] = value;
  });
  if (!dropped) return src;
  return next;
}

function toCompositionRows(value) {
  if (Array.isArray(value)) {
    return value
      .map((row) => {
        if (row && typeof row === 'object') {
          const valueId = row.valueId ?? row.attributeValueId;
          if (valueId == null || valueId === '') return null;
          return {
            valueId: String(valueId),
            percent: String(row.percent ?? row.attributeExtraValue ?? ''),
          };
        }
        if (row == null || row === '') return null;
        return { valueId: String(row), percent: '' };
      })
      .filter(Boolean);
  }
  if (value != null && value !== '' && (typeof value === 'string' || typeof value === 'number')) {
    return [{ valueId: String(value), percent: '' }];
  }
  return [];
}

function valueMatchesAttrOption(attr, text) {
  if (!attr || !Array.isArray(attr.values) || !text) return false;
  return attr.values.some((row) => String(row?.id ?? '') === text);
}

/** 手输属性（克重/gsm 等）：组包时走 attribute_extra_value，不能把数字当成 value_id */
function isHandInputProductAttribute(attr, text) {
  if (!text) return false;
  if (attr && Number(attr.mode) === 0) return true;
  if (attr && (!Array.isArray(attr.values) || attr.values.length === 0) && /^\d+(\.\d+)?$/.test(text)) {
    return true;
  }
  if (attr && /^\d+(\.\d+)?$/.test(text) && !valueMatchesAttrOption(attr, text)) {
    const name = `${attr.name || ''} ${attr.nameEn || ''}`;
    if (/克重|重量|gsm|g\/m|weight/i.test(name)) return true;
  }
  return false;
}

function normalizeCompositionRow(attr, row) {
  if (!row || typeof row !== 'object') return row;
  let valueId = String(row.valueId ?? row.attributeValueId ?? '').trim();
  const percent = String(row.percent ?? row.attributeExtraValue ?? '');
  if (valueId && /^\d+$/.test(valueId) && valueMatchesAttrOption(attr, valueId)) {
    return { valueId, percent };
  }
  const label = valueId || String(row.label || row.name || '').trim();
  if (!label) return { valueId: '', percent };
  if (isHandInputProductAttribute(attr, label)) {
    return { valueId: label, percent };
  }
  const matched = findAttributeValueByLabel(attr, label);
  if (matched?.id && !isBlankTiktokAttributeValueId(matched.id)) {
    return { valueId: String(matched.id), percent };
  }
  return { valueId: label, percent };
}

function resolveCompositionAttributeValue(attr, value) {
  if (!Array.isArray(value)) return toCompositionRows(value).map((row) => normalizeCompositionRow(attr, row));
  return value.map((row) => normalizeCompositionRow(attr, row));
}

function resolveScalarProductAttributeValue(attr, value) {
  if (value == null || value === '') return value;
  const text = String(value).trim();
  if (isHandInputProductAttribute(attr, text)) return text;
  if (/^\d+$/.test(text) && valueMatchesAttrOption(attr, text)) return text;
  const matched = findAttributeValueByLabel(attr, text);
  if (matched?.id && !isBlankTiktokAttributeValueId(matched.id)) return String(matched.id);
  return value;
}

function resolveMultiSelectProductAttributeValue(attr, value) {
  if (!Array.isArray(value)) return value;
  return value.map((entry) => {
    const text = String(entry ?? '').trim();
    if (!text) return entry;
    if (/^\d+$/.test(text) && valueMatchesAttrOption(attr, text)) return text;
    const matched = findAttributeValueByLabel(attr, text);
    if (matched?.id && !isBlankTiktokAttributeValueId(matched.id)) return String(matched.id);
    return entry;
  });
}

/** 单个商品属性：把模板里仅存的中文名/展示名还原为平台 attribute_value_id */
export function resolveProductAttributeValue(attr, value) {
  if (value == null || value === '') return value;
  const composition = attr && (Number(attr.mode) === 4 || attr.isComposition);
  if (composition) return resolveCompositionAttributeValue(attr, value);
  if (Array.isArray(value)) {
    if (value.some((row) => row && typeof row === 'object' && ('percent' in row || 'valueId' in row))) {
      return resolveCompositionAttributeValue(attr, value);
    }
    return resolveMultiSelectProductAttributeValue(attr, value);
  }
  return resolveScalarProductAttributeValue(attr, value);
}

/** 发品前批量解析商品属性（与 SKC 颜色 resolve 同理，避免误传 attribute_value_id=0） */
export function resolveProductAttributesForPublish(productAttributes = {}, attrTpl) {
  if (attrTpl == null || typeof attrTpl !== 'object') return productAttributes || {};
  const defs = collectCategoryProductAttrDefs(attrTpl);
  const defMap = new Map(defs.map((attr) => [String(attr.attributeId), attr]));
  const scoped = filterProductAttributesToCategory(productAttributes, attrTpl);
  const next = { ...scoped };
  let changed = scoped !== (productAttributes || {});
  Object.keys(next).forEach((id) => {
    const attr = defMap.get(String(id));
    if (!attr) return;
    const resolved = resolveProductAttributeValue(attr, next[id]);
    if (resolved !== next[id]) {
      next[id] = resolved;
      changed = true;
    }
  });
  return changed ? next : (productAttributes || {});
}

/**
 * 类目属性模板就绪后：
 * 1. 丢掉不属于本店/本类目的属性（官方模板全局类目残留）
 * 2. 成分/材质转成 [{valueId, percent}]
 * 3. 下拉/多选仅存展示名时还原为平台 attribute_value_id
 */
export function alignAttributesToCategoryTemplate(productAttributes = {}, attrTpl) {
  if (attrTpl == null || typeof attrTpl !== 'object') return productAttributes || {};
  const defs = collectCategoryProductAttrDefs(attrTpl);
  const filtered = filterProductAttributesToCategory(productAttributes, attrTpl);
  const next = { ...filtered };
  let changed = filtered !== (productAttributes || {});
  defs.forEach((attr) => {
    const id = String(attr?.attributeId ?? '');
    if (!id || !(id in next)) return;
    const value = next[id];
    const composition = attr.mode === 4 || attr.isComposition;
    if (composition) {
      let rows;
      if (Array.isArray(value) && value.length > 0
        && value.every((row) => row && typeof row === 'object' && (row.valueId != null || row.attributeValueId != null))) {
        rows = resolveProductAttributeValue(attr, value);
      } else {
        rows = resolveProductAttributeValue(attr, toCompositionRows(value));
      }
      if (JSON.stringify(rows) !== JSON.stringify(value)) {
        next[id] = rows;
        changed = true;
      }
      return;
    }
    const resolved = resolveProductAttributeValue(attr, value);
    if (resolved !== value) {
      next[id] = resolved;
      changed = true;
    }
  });
  return changed ? next : (productAttributes || {});
}

function normalizeTiktokFieldNameKey(text) {
  return String(text || '')
    .trim()
    .toLowerCase()
    .replace(/[（(][^）)]*[）)]/g, '')
    .replace(/[\s\-_/·・.。，,]/g, '');
}

function attributeDefDisplayNames(attr) {
  return [attr?.name, attr?.nameCn, attr?.nameEn, attr?.attributeName, attr?.label]
    .map((s) => String(s || '').trim())
    .filter(Boolean);
}

function buildProductAttrNameIndex(attrTpl) {
  const map = new Map();
  collectCategoryProductAttrDefs(attrTpl).forEach((attr) => {
    attributeDefDisplayNames(attr).forEach((name) => {
      const key = normalizeTiktokFieldNameKey(name);
      if (!key || map.has(key)) return;
      map.set(key, attr);
    });
  });
  return map;
}

function lookupAttributeValueLabel(attr, valueId) {
  const id = String(valueId ?? '').trim();
  if (!id) return '';
  const values = Array.isArray(attr?.values) ? attr.values : [];
  const hit = values.find((row) => String(row?.id ?? '') === id);
  if (!hit) return id;
  return String(hit.name || hit.nameCn || hit.nameEn || hit.label || id).trim() || id;
}

/** 把已填值转成可跨类目传递的展示名（或手输原文），便于在新类目按名称找回 valueId */
function toRemappableAttributeValue(attr, value) {
  if (!isFilledProductAttributeValue(value)) return null;
  const composition = attr && (Number(attr.mode) === 4 || attr.isComposition);
  if (composition) {
    return toCompositionRows(value).map((row) => ({
      valueId: lookupAttributeValueLabel(attr, row.valueId) || String(row.valueId || ''),
      percent: String(row.percent ?? ''),
    }));
  }
  if (Array.isArray(value)) {
    if (value.some((row) => row && typeof row === 'object' && ('percent' in row || 'valueId' in row))) {
      return toCompositionRows(value).map((row) => ({
        valueId: lookupAttributeValueLabel(attr, row.valueId) || String(row.valueId || ''),
        percent: String(row.percent ?? ''),
      }));
    }
    return value.map((entry) => {
      const text = String(entry ?? '').trim();
      if (!text) return entry;
      return lookupAttributeValueLabel(attr, text) || text;
    });
  }
  const text = String(value).trim();
  if (!text) return value;
  return lookupAttributeValueLabel(attr, text) || text;
}

function findMatchingProductAttr(prevAttr, oldId, nextIds, nextDefs, nextByName) {
  const id = String(oldId || '').trim();
  if (id && nextIds.has(id)) return nextDefs.get(id) || null;
  if (!prevAttr) return null;
  for (const name of attributeDefDisplayNames(prevAttr)) {
    const hit = nextByName.get(normalizeTiktokFieldNameKey(name));
    if (hit) return hit;
  }
  return null;
}

/**
 * 切换类目时：按属性名称（中/英）把旧类目已填参数映射到新类目相似字段。
 * - 同 attributeId 直接保留
 * - 不同 ID 但名称相同/相似时，按选项展示名重新解析 valueId
 * - 新类目没有对应字段的项丢弃
 */
export function remapProductAttributesAcrossCategories({
  prevAttributes = {},
  prevAttrTpl = null,
  nextAttrTpl = null,
} = {}) {
  if (nextAttrTpl == null || typeof nextAttrTpl !== 'object') {
    return { attributes: {}, preservedCount: 0, mapped: [] };
  }
  const filled = pickFilledProductAttributes(prevAttributes);
  if (!Object.keys(filled).length) {
    return { attributes: {}, preservedCount: 0, mapped: [] };
  }

  const prevDefs = new Map(
    collectCategoryProductAttrDefs(prevAttrTpl).map((attr) => [String(attr.attributeId), attr]),
  );
  const nextDefs = new Map(
    collectCategoryProductAttrDefs(nextAttrTpl).map((attr) => [String(attr.attributeId), attr]),
  );
  const nextIds = new Set(listCategoryProductAttributeIds(nextAttrTpl));
  const nextByName = buildProductAttrNameIndex(nextAttrTpl);

  const out = {};
  const mapped = [];
  Object.entries(filled).forEach(([oldId, value]) => {
    const prevAttr = prevDefs.get(String(oldId)) || null;
    const nextAttr = findMatchingProductAttr(prevAttr, oldId, nextIds, nextDefs, nextByName);
    if (!nextAttr?.attributeId) return;
    const newId = String(nextAttr.attributeId);
    if (Object.prototype.hasOwnProperty.call(out, newId)) return;

    const transferable = toRemappableAttributeValue(prevAttr || nextAttr, value);
    if (transferable == null) return;
    const resolved = resolveProductAttributeValue(nextAttr, transferable);
    if (!isFilledProductAttributeValue(resolved)) return;
    out[newId] = resolved;
    mapped.push({
      fromId: String(oldId),
      toId: newId,
      name: nextAttr.name || prevAttr?.name || '',
    });
  });

  const aligned = alignAttributesToCategoryTemplate(out, nextAttrTpl);
  const preservedCount = Object.values(aligned).filter(isFilledProductAttributeValue).length;
  return { attributes: aligned, preservedCount, mapped };
}

function buildSizeMeasureNameIndex(sizeAttrs = []) {
  const map = new Map();
  (Array.isArray(sizeAttrs) ? sizeAttrs : []).forEach((attr) => {
    attributeDefDisplayNames(attr).forEach((name) => {
      const key = normalizeTiktokFieldNameKey(name);
      if (!key || map.has(key)) return;
      map.set(key, attr);
    });
  });
  return map;
}

function remapSizeMeasureCells(sourceMeasures, prevSizeAttrs, nextSizeAttrs) {
  if (!sourceMeasures || typeof sourceMeasures !== 'object') return {};
  const prevDefs = new Map(
    (Array.isArray(prevSizeAttrs) ? prevSizeAttrs : []).map((attr) => [String(attr.attributeId), attr]),
  );
  const nextById = new Set(
    (Array.isArray(nextSizeAttrs) ? nextSizeAttrs : []).map((attr) => String(attr.attributeId)),
  );
  const nextByName = buildSizeMeasureNameIndex(nextSizeAttrs);
  const cell = {};
  Object.entries(sourceMeasures).forEach(([oldMeasureId, raw]) => {
    if (raw == null || String(raw).trim() === '') return;
    const prevAttr = prevDefs.get(String(oldMeasureId)) || null;
    let nextAttr = null;
    if (nextById.has(String(oldMeasureId))) {
      nextAttr = (nextSizeAttrs || []).find((attr) => String(attr.attributeId) === String(oldMeasureId));
    }
    if (!nextAttr && prevAttr) {
      for (const name of attributeDefDisplayNames(prevAttr)) {
        const hit = nextByName.get(normalizeTiktokFieldNameKey(name));
        if (hit) {
          nextAttr = hit;
          break;
        }
      }
    }
    if (!nextAttr?.attributeId) return;
    cell[String(nextAttr.attributeId)] = String(raw);
  });
  return cell;
}

/**
 * 切换类目时保留相似尺码表：测量列按名称对齐；尺码行优先同 id，其次按尺码展示名对齐。
 */
export function remapSizeChartAcrossCategories({
  prevSizeChart = {},
  prevSelectedSizeIds = [],
  prevAttrTpl = null,
  nextAttrTpl = null,
} = {}) {
  const source = prevSizeChart && typeof prevSizeChart === 'object' ? prevSizeChart : {};
  if (!Object.keys(source).length) {
    return { sizeChart: {}, selectedSizeIds: [], preservedCount: 0 };
  }
  const prevSizeAttrs = prevAttrTpl?.sizeAttrs || [];
  const nextSizeAttrs = nextAttrTpl?.sizeAttrs || [];
  const prevSizeAttr = prevAttrTpl?.skuSaleAttrs?.[0] || null;
  const nextSizeAttr = nextAttrTpl?.skuSaleAttrs?.[0] || null;
  const nextValues = Array.isArray(nextSizeAttr?.values) ? nextSizeAttr.values : [];

  const next = {};
  let preservedCount = 0;

  const remapRowKey = (oldKey) => {
    const key = String(oldKey || '');
    if (!key) return '';
    if (key === '__base__') return '__base__';
    if (!nextSizeAttr) return key;
    if (nextValues.some((row) => String(row?.id) === key)) return key;
    const label = lookupAttributeValueLabel(prevSizeAttr, key) || key;
    const matched = findAttributeValueByLabel(nextSizeAttr, label);
    if (matched?.id && !isBlankTiktokAttributeValueId(matched.id)) return String(matched.id);
    return '';
  };

  Object.entries(source).forEach(([oldRowId, measures]) => {
    const newRowId = remapRowKey(oldRowId);
    if (!newRowId) return;
    const cell = remapSizeMeasureCells(measures, prevSizeAttrs, nextSizeAttrs);
    if (!Object.keys(cell).length) return;
    next[newRowId] = cell;
    preservedCount += Object.keys(cell).length;
  });

  const selectedSizeIds = (Array.isArray(prevSelectedSizeIds) ? prevSelectedSizeIds : [])
    .map((id) => remapRowKey(id))
    .filter((id) => id && id !== '__base__');

  Object.keys(next).forEach((id) => {
    if (id === '__base__') return;
    if (!selectedSizeIds.includes(id)) selectedSizeIds.push(id);
  });

  return { sizeChart: next, selectedSizeIds, preservedCount };
}

function ensureSkcColorLabel(skc, prevColorAttr) {
  if (!skc || typeof skc !== 'object') return skc;
  if (String(skc.colorLabel || skc.colorExtraValue || '').trim()) return skc;
  const id = String(skc.colorValueId || '').trim();
  if (!id || isBlankTiktokAttributeValueId(id)) return skc;
  const label = lookupAttributeValueLabel(prevColorAttr, id);
  if (!label || label === id) return skc;
  return { ...skc, colorLabel: label };
}

function remapSkuSizeRow(sku, prevSizeAttr, nextSizeAttr) {
  if (!sku || typeof sku !== 'object') return sku;
  if (!nextSizeAttr?.attributeId) {
    return { ...sku, sizeAttributeId: sku.sizeAttributeId || '' };
  }
  const nextAttrId = String(nextSizeAttr.attributeId);
  if (sku.sizeIsCustom) {
    const label = String(sku.sizeExtraValue || sku.sizeLabel || '').trim();
    return {
      ...sku,
      sizeAttributeId: nextAttrId,
      sizeValueId: '',
      sizeIsCustom: true,
      sizeExtraValue: label,
      sizeLabel: label || sku.sizeLabel || '',
    };
  }
  const oldId = String(sku.sizeValueId || '').trim();
  const nextValues = Array.isArray(nextSizeAttr.values) ? nextSizeAttr.values : [];
  if (oldId && nextValues.some((row) => String(row?.id) === oldId)) {
    const hit = nextValues.find((row) => String(row?.id) === oldId);
    return {
      ...sku,
      sizeAttributeId: nextAttrId,
      sizeValueId: oldId,
      sizeLabel: hit?.name || sku.sizeLabel || '',
      sizeIsCustom: false,
      sizeExtraValue: '',
    };
  }
  const sizeLabel = String(
    sku.sizeLabel
    || sku.sizeExtraValue
    || lookupAttributeValueLabel(prevSizeAttr, oldId)
    || '',
  ).trim();
  if (sizeLabel) {
    const matched = findAttributeValueByLabel(nextSizeAttr, sizeLabel);
    if (matched?.id && !isBlankTiktokAttributeValueId(matched.id)) {
      return {
        ...sku,
        sizeAttributeId: nextAttrId,
        sizeValueId: String(matched.id),
        sizeLabel: matched.name || sizeLabel,
        sizeIsCustom: false,
        sizeExtraValue: '',
      };
    }
    return {
      ...sku,
      sizeAttributeId: nextAttrId,
      sizeValueId: '',
      sizeIsCustom: true,
      sizeExtraValue: sizeLabel,
      sizeLabel,
    };
  }
  return {
    ...sku,
    sizeAttributeId: nextAttrId,
  };
}

/**
 * 切换类目时保留主规格（SKC）：
 * - 颜色 / 尺码按展示名映射到新类目销售属性
 * - 映射不到平台选项时降级为自定义规格，尽量保住已填图片、价格、库存等
 * T 恤 ↔ 上衣等相近类目通常主规格结构一致，适合整表保留。
 */
export function remapSkcListAcrossCategories({
  prevSkcList = [],
  prevAttrTpl = null,
  nextAttrTpl = null,
} = {}) {
  const list = Array.isArray(prevSkcList) ? prevSkcList.filter(Boolean) : [];
  if (!list.length) {
    return { skcList: [], preservedCount: 0, selectedSizeIds: [] };
  }

  const prevColorAttr = prevAttrTpl?.skcSaleAttrs?.[0] || null;
  const nextColorAttr = nextAttrTpl?.skcSaleAttrs?.[0] || null;
  const prevSizeAttr = prevAttrTpl?.skuSaleAttrs?.[0] || null;
  const nextSizeAttr = nextAttrTpl?.skuSaleAttrs?.[0] || null;

  const skcList = list.map((rawSkc) => {
    const skc = ensureSkcColorLabel(rawSkc, prevColorAttr);
    let nextSkc = { ...skc };

    if (nextColorAttr?.attributeId) {
      const nextColorId = String(nextColorAttr.attributeId);
      if (skc.colorIsCustom) {
        const label = String(skc.colorExtraValue || skc.colorLabel || '').trim();
        nextSkc = {
          ...skc,
          colorAttributeId: nextColorId,
          colorValueId: '',
          colorIsCustom: true,
          colorExtraValue: label,
          colorLabel: label || skc.colorLabel || '',
        };
      } else {
        nextSkc = resolveSkcColorAgainstTemplate({
          ...skc,
          colorAttributeId: nextColorId,
        }, nextColorAttr);
        const stillBlank = isBlankTiktokAttributeValueId(nextSkc.colorValueId);
        const label = String(nextSkc.colorExtraValue || nextSkc.colorLabel || '').trim();
        if (stillBlank && label) {
          nextSkc = {
            ...nextSkc,
            colorAttributeId: nextColorId,
            colorValueId: '',
            colorIsCustom: true,
            colorExtraValue: label,
            colorLabel: label,
          };
        } else {
          nextSkc = {
            ...nextSkc,
            colorAttributeId: nextColorId,
          };
        }
      }
    }

    const skuRows = Array.isArray(skc.skuRows)
      ? skc.skuRows.map((sku) => remapSkuSizeRow(sku, prevSizeAttr, nextSizeAttr))
      : [];
    return { ...nextSkc, skuRows };
  });

  const selectedSizeIds = [];
  const seen = new Set();
  skcList.forEach((skc) => {
    (skc.skuRows || []).forEach((sku) => {
      const key = skuSizeKey(sku);
      if (!key || seen.has(key)) return;
      seen.add(key);
      selectedSizeIds.push(key);
    });
  });

  return {
    skcList,
    preservedCount: skcList.length,
    selectedSizeIds,
  };
}

/**
 * 切换类目后合并「自动套用模板」与「跨类目保留值」：保留值优先。
 */
export function mergePreservedProductAttributes(autoPatch = {}, preservedAttributes = {}) {
  const base = autoPatch && typeof autoPatch === 'object' ? { ...autoPatch } : {};
  Object.entries(preservedAttributes || {}).forEach(([id, value]) => {
    if (!isFilledProductAttributeValue(value)) return;
    base[String(id)] = value;
  });
  return base;
}
